import React, { useState, useEffect } from 'react';
import { leaderboardAPI } from '../services/api';

const MiniLeaderboard = ({ 
  type, // 'session' or 'lesson'
  sessionId, 
  lessonId, 
  profileId,
  title = "Leaderboard"
}) => {
  const [leaderboard, setLeaderboard] = useState([]);
  const [allScores, setAllScores] = useState([]);
  const [userPoints, setUserPoints] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchLeaderboard();
  }, [type, sessionId, lessonId, profileId]);

  const fetchLeaderboard = async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (type === 'session' && sessionId) {
        // Fetch session leaderboard and user's points
        const [leaderboardData, userPointsData] = await Promise.all([
          leaderboardAPI.getSessionLeaderboard(sessionId.trim()),
          leaderboardAPI.getProfileSessionPoints(sessionId.trim(), profileId)
        ]);
        
        setLeaderboard(leaderboardData.top_scores || []);
        setAllScores(leaderboardData.all_scores || []);
        setUserPoints(userPointsData.points || 0);
        
      } else if (type === 'lesson' && lessonId) {
        // Fetch lesson leaderboard and user's points
        const [leaderboardData, userPointsData] = await Promise.all([
          leaderboardAPI.getLessonLeaderboard(lessonId),
          leaderboardAPI.getProfileLessonPoints(lessonId, profileId)
        ]);
        
        setLeaderboard(leaderboardData.top_scores || []);
        setAllScores(leaderboardData.all_scores || []);
        setUserPoints(userPointsData.points || 0);
      }
      
    } catch (err) {
      console.error('Error fetching leaderboard:', err);
      setError('Failed to load leaderboard');
    } finally {
      setLoading(false);
    }
  };

  // Calculate ranks based on scores (handle ties)
  const calculateRanks = () => {
    if (leaderboard.length === 0) return [];
    
    const rankedEntries = [];
    let currentRank = 1;
    let previousPoints = null;
    let sameRankCount = 0;
    
    leaderboard.forEach((entry, index) => {
      if (previousPoints === null || entry.points < previousPoints) {
        // New rank tier
        currentRank = index + 1;
        sameRankCount = 1;
      } else if (entry.points === previousPoints) {
        // Tied with previous entry
        sameRankCount++;
      }
      
      rankedEntries.push({
        ...entry,
        rank: currentRank
      });
      
      previousPoints = entry.points;
    });
    
    return rankedEntries;
  };

  const getCarrotIcon = (rank) => {
    const carrotMap = {
      1: '/carrot_gold.png',
      2: '/carrot_silver.png',
      3: '/carrot_bronze.png'
    };
    return carrotMap[rank] || '/carrot_orange.png';
  };

  const isUserInTop = () => {
    return leaderboard.some(entry => entry.profile_id === profileId);
  };

  const isUserTiedButNotShown = () => {
    if (isUserInTop() || userPoints === 0) return false;
    
    // Check if user has same points as anyone on the leaderboard
    const leaderboardPoints = leaderboard.map(entry => entry.points);
    return leaderboardPoints.includes(userPoints);
  };

  if (loading) {
    return (
      <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-lg p-4 border-2 border-orange-200 shadow-sm">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-lg">🏆</span>
          <h3 className="font-bold text-gray-800">{title}</h3>
        </div>
        <p className="text-sm text-gray-500 text-center">Loading...</p>
      </div>
    );
  }

  if (error || leaderboard.length === 0) {
    return (
      <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-lg p-4 border-2 border-orange-200 shadow-sm">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-lg">🏆</span>
          <h3 className="font-bold text-gray-800">{title}</h3>
        </div>
        <p className="text-sm text-gray-500 text-center">No data yet</p>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-lg p-4 border-2 border-orange-200 shadow-sm">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-lg">🏆</span>
        <h3 className="font-bold text-gray-800">{title}</h3>
      </div>
      
      {/* Leaderboard entries */}
      <div className="space-y-2 mb-3">
        {calculateRanks().map((entry, index) => {
          const isCurrentUser = entry.profile_id === profileId;
          return (
            <div 
              key={index}
              className={`flex items-center gap-2 p-2 rounded ${
                isCurrentUser ? 'bg-blue-100 border border-blue-300' : 'bg-white'
              }`}
            >
              <img 
                src={getCarrotIcon(entry.rank)} 
                alt={`Rank ${entry.rank}`}
                className="w-8 h-8"
              />
              <span className="text-sm font-semibold text-gray-700">
                #{entry.rank}
              </span>
              <span className="text-sm text-gray-800 flex-1 truncate">
                {entry.profile_name}
              </span>
              <span className="text-sm font-bold text-orange-600">
                {entry.points} pts
              </span>
            </div>
          );
        })}
      </div>
      
      {/* User's score - always show prominently */}
      <div className="pt-3 mt-2 border-t-2 border-orange-300">
        {isUserTiedButNotShown() ? (
          // Special message for users tied but not displayed
          <div className="p-3 rounded-lg bg-gradient-to-r from-amber-100 to-yellow-50 border-2 border-amber-400 shadow-sm">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-lg font-bold text-amber-900">
                ⭐ {userPoints} pts
              </span>
            </div>
            <p className="text-xs font-semibold text-amber-800 leading-relaxed">
              You've matched the top score, but didn't beat it. Try again to overtake the position!
            </p>
          </div>
        ) : !isUserInTop() && userPoints > 0 ? (
          // Regular "Your Score" display
          <div className="flex items-center gap-2 p-3 rounded-lg bg-gradient-to-r from-blue-100 to-blue-50 border-2 border-blue-400 shadow-sm">
            <span className="text-base font-bold text-blue-900">
              Your Score:
            </span>
            <span className="text-xl font-bold text-green-700 ml-auto">
              ⭐ {userPoints} pts
            </span>
          </div>
        ) : userPoints === 0 ? (
          // Zero points message
          <div className="p-3 rounded-lg bg-gray-50 border border-gray-300 text-center">
            <p className="text-sm font-semibold text-gray-600">
              Your Score: <span className="text-gray-500">0 pts</span>
            </p>
            <p className="text-xs text-gray-500 mt-1">
              Complete activities to earn points!
            </p>
          </div>
        ) : (
          // User is in top rankings
          <div className="p-2 rounded-lg bg-blue-50 border border-blue-200 text-center">
            <p className="text-xs font-semibold text-blue-700">
              🎉 You're in the top rankings!
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MiniLeaderboard;
